import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authorsAPI, uploadAPI } from '../utils/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit, Trash2, X, Upload, User,
  Twitter, Linkedin, Globe, Mail
} from 'lucide-react';

export const AdminAuthors = () => {
  const { user: currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    avatar: '',
    bio: '',
    email: '',
    social_links: {}
  });

  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/admin/login');
    }
  }, [currentUser, authLoading, navigate]);

  useEffect(() => {
    if (currentUser) {
      fetchAuthors();
    }
  }, [currentUser]);

  const fetchAuthors = async () => {
    try {
      const response = await authorsAPI.getAll(false); // Include inactive
      setAuthors(response.data);
    } catch (error) {
      console.error('Failed to fetch authors:', error);
      toast.error('Failed to load authors');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: editingAuthor ? formData.slug : generateSlug(name)
    });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAPI.upload(file);
      // R2 returns absolute URLs; legacy local returns relative paths
      const rawUrl = response.data.url;
      const imageUrl = rawUrl.startsWith('http') ? rawUrl : `${process.env.REACT_APP_BACKEND_URL}${rawUrl}`;
      setFormData(prev => ({ ...prev, avatar: imageUrl }));
      toast.success('Avatar uploaded');
    } catch (error) {
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAuthor) {
        await authorsAPI.update(editingAuthor.id, formData);
        toast.success('Author updated successfully');
      } else {
        await authorsAPI.create(formData);
        toast.success('Author created successfully');
      }
      fetchAuthors();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save author');
    }
  };

  const handleDelete = async (authorId) => {
    if (!window.confirm('Are you sure you want to delete this author profile?')) return;
    
    try {
      await authorsAPI.delete(authorId);
      toast.success('Author deleted successfully');
      fetchAuthors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete author');
    }
  };

  const openCreateModal = () => {
    setEditingAuthor(null);
    setFormData({
      name: '',
      slug: '',
      avatar: '',
      bio: '',
      email: '',
      social_links: {}
    });
    setShowModal(true);
  };

  const openEditModal = (author) => {
    setEditingAuthor(author);
    setFormData({
      name: author.name,
      slug: author.slug,
      avatar: author.avatar || '',
      bio: author.bio || '',
      email: author.email || '',
      social_links: author.social_links || {}
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAuthor(null);
    setFormData({
      name: '',
      slug: '',
      avatar: '',
      bio: '',
      email: '',
      social_links: {}
    });
  };

  const updateSocialLink = (platform, value) => {
    setFormData({
      ...formData,
      social_links: {
        ...formData.social_links,
        [platform]: value
      }
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red"></div>
      </div>
    );
  }

  if (!currentUser) return null;

  // Check permission
  const canManageAuthors = currentUser.role === 'super_admin' || currentUser.role === 'editor';
  if (!canManageAuthors) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-playfair font-bold text-brand-green mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to manage authors.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-authors-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 text-brand-green hover:text-brand-red transition-colors mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-playfair font-bold text-brand-green flex items-center gap-3">
                <User className="w-10 h-10" />
                Author Profiles
              </h1>
              <p className="text-gray-600 mt-2">Manage author profiles for article attribution</p>
            </div>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 font-bold hover:bg-brand-red/90 transition-colors uppercase tracking-wide rounded-lg"
              data-testid="create-author-button"
            >
              <Plus className="w-5 h-5" />
              Add Author
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <p className="text-blue-800 text-sm">
            <strong>Note:</strong> Author profiles are separate from CMS user accounts. 
            They are used for public article attribution and can include external contributors 
            who don't need CMS access.
          </p>
        </div>

        {/* Authors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="authors-grid">
          {authors.map((author) => (
            <div 
              key={author.id} 
              className={`bg-white border-2 border-gray-900 rounded-lg p-6 ${!author.is_active ? 'opacity-60' : ''}`}
              data-testid={`author-card-${author.id}`}
            >
              <div className="flex items-start gap-4">
                {author.avatar ? (
                  <img 
                    src={author.avatar} 
                    alt={author.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#3A86FF] to-[#8A4DFF] flex items-center justify-center text-white text-xl font-bold">
                    {author.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-playfair font-bold text-lg text-brand-green truncate">
                    {author.name}
                  </h3>
                  <p className="text-sm text-gray-500">@{author.slug}</p>
                  {!author.is_active && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                      Inactive
                    </span>
                  )}
                </div>
              </div>
              
              {author.bio && (
                <p className="text-gray-600 text-sm mt-4 line-clamp-3">{author.bio}</p>
              )}
              
              {/* Social Links */}
              {author.social_links && Object.keys(author.social_links).length > 0 && (
                <div className="flex gap-2 mt-4">
                  {author.social_links.twitter && (
                    <a href={author.social_links.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 rounded-full hover:bg-gray-200">
                      <Twitter className="w-4 h-4 text-[#1DA1F2]" />
                    </a>
                  )}
                  {author.social_links.linkedin && (
                    <a href={author.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 rounded-full hover:bg-gray-200">
                      <Linkedin className="w-4 h-4 text-[#0077B5]" />
                    </a>
                  )}
                  {author.social_links.website && (
                    <a href={author.social_links.website} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 rounded-full hover:bg-gray-200">
                      <Globe className="w-4 h-4 text-gray-600" />
                    </a>
                  )}
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => openEditModal(author)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                  data-testid={`edit-author-${author.id}`}
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(author.id)}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
                  data-testid={`delete-author-${author.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          
          {authors.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              No author profiles yet. Click "Add Author" to create one.
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div 
            className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
            data-testid="author-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-playfair font-bold text-gray-900">
                {editingAuthor ? 'Edit Author' : 'Create Author Profile'}
              </h3>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Avatar Upload */}
              <div className="flex items-center gap-4">
                {formData.avatar ? (
                  <img 
                    src={formData.avatar} 
                    alt="Avatar preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <label className="cursor-pointer inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleAvatarUpload} 
                      className="hidden" 
                      disabled={uploading}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Square image recommended</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  required
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="Author's full name"
                  data-testid="author-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <div className="flex items-center">
                  <span className="bg-gray-100 px-3 py-2 border-2 border-r-0 border-gray-300 rounded-l-lg text-gray-500 text-sm">author/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    placeholder="author-slug"
                    data-testid="author-slug-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="A short bio about the author..."
                  data-testid="author-bio-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Public Email (optional)
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="author@example.com"
                />
              </div>

              {/* Social Links */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Social Links</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Twitter className="w-5 h-5 text-[#1DA1F2]" />
                    <input
                      type="url"
                      value={formData.social_links.twitter || ''}
                      onChange={(e) => updateSocialLink('twitter', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      placeholder="https://twitter.com/username"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Linkedin className="w-5 h-5 text-[#0077B5]" />
                    <input
                      type="url"
                      value={formData.social_links.linkedin || ''}
                      onChange={(e) => updateSocialLink('linkedin', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      placeholder="https://linkedin.com/in/username"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-gray-600" />
                    <input
                      type="url"
                      value={formData.social_links.website || ''}
                      onChange={(e) => updateSocialLink('website', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      placeholder="https://yourwebsite.com"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 py-3 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green/90 transition-colors"
                  data-testid="save-author-button"
                >
                  {editingAuthor ? 'Update Author' : 'Create Author'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 border-2 border-gray-300 font-bold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuthors;
